// Panel chi tiết node đang chọn. Mặc định chỉ hiển thị danh tính nghiệp vụ: tên, trạng thái,
// mã kinh doanh (secondary), phân loại, tên cha, hiệu lực từ/đến và dải hiệu lực.
// Lịch sử version là progressive disclosure: Collapse "Lịch sử thay đổi" với destroyOnHidden
// nên bảng không tồn tại trong DOM khi đóng. UUID nội bộ và "vN" không xuất hiện ở vùng mặc định.
// Action bar phân cấp theo nhiệm vụ: ACTIVE có "Tạo {entityNoun} trực thuộc" (primary) + "Đổi tên" trực tiếp và
// "Thao tác khác" (Dropdown: Chuyển quản lý/Sáp nhập/Đóng); INACTIVE chỉ "Mở lại";
// MERGED không thao tác, kèm ghi chú chỉ đọc lịch sử. Hint client — backend vẫn enforce.
// Presentational thuần: mọi fetch/command thuộc AdminWorkspace.
// Focus-return: AdminWorkspace mount/unmount AdminCommandModal theo modalState (không toggle
// open=false) nên rc-dialog không kịp chạy focusTriggerAfterClose và focus rơi về <body> khi
// đóng. Panel sở hữu trigger nên tự trả focus về trigger sau khi modal biến mất khỏi DOM.

import { Button, Collapse, Dropdown, Flex, Skeleton, Typography } from "antd";
import { useEffect, useRef } from "react";
import type { MenuProps } from "antd";
import { formatClassification, type AdminDetail, type AdminVersion } from "../adminTypes";
import type { AdminCommandKind } from "./AdminCommandModal";
import { StatusTag } from "./StatusTag";
import { ValidityBand } from "./ValidityBand";
import { VersionHistoryTable } from "./VersionHistoryTable";

/** Hành động hiếm, tác động lớn của node ACTIVE — gom vào overflow menu, không nằm trực tiếp. */
const OVERFLOW_MENU_ITEMS: NonNullable<MenuProps["items"]> = [
  { key: "transfer", label: "Chuyển quản lý" },
  { key: "merge", label: "Sáp nhập" },
  { key: "close", label: "Đóng" },
];

export function AdminDetailPanel({
  detail,
  versions,
  loading,
  parentNodeName,
  classificationLabel,
  entityNoun,
  onAction,
}: {
  readonly detail: AdminDetail | null;
  readonly versions: readonly AdminVersion[];
  readonly loading: boolean;
  readonly parentNodeName: string | null;
  readonly classificationLabel: string;
  readonly entityNoun: string;
  readonly onAction: (kind: AdminCommandKind) => void;
}) {
  const overflowTriggerRef = useRef<HTMLButtonElement>(null);
  const refocusCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => refocusCleanupRef.current?.(), []);

  if (loading) {
    return (
      <div role="status" aria-label={`Đang tải chi tiết ${entityNoun}`}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }
  if (!detail) {
    return null;
  }

  const directActions: { kind: AdminCommandKind; label: string }[] =
    detail.status === "ACTIVE"
      ? [
          { kind: "create", label: `Tạo ${entityNoun} trực thuộc` },
          { kind: "rename", label: "Đổi tên" },
        ]
      : detail.status === "INACTIVE"
        ? [{ kind: "open", label: "Mở lại" }]
        : [];

  // Trả focus về trigger khi modal lệnh đóng: AdminWorkspace mount/unmount AdminCommandModal
  // (không toggle open=false) nên rc-dialog không chạy focusTriggerAfterClose; focus rơi về
  // <body>. Panel sở hữu trigger: theo dõi tới khi .ant-modal-root bị gỡ khỏi DOM rồi focus
  // lại trigger. FocusLock của rc-util vẫn "giữ" focus trong modal vài frame sau removal
  // (leave-motion dọn portal trễ) → burst hữu hạn 30 frame, dừng ngay khi focus đã về trigger.
  const scheduleRefocusAfterModalClose = (triggerEl: HTMLElement | null) => {
    if (!triggerEl || typeof MutationObserver === "undefined") {
      return;
    }
    refocusCleanupRef.current?.();
    let modalSeen = false;
    let burstFrame: number | null = null;
    const observer = new MutationObserver(() => {
      if (document.querySelector(".ant-modal-root") !== null) {
        modalSeen = true;
        return;
      }
      if (!modalSeen) {
        return;
      }
      cleanup();
      let attempts = 30;
      const burst = () => {
        triggerEl.focus({ preventScroll: true });
        if (document.activeElement === triggerEl || (attempts -= 1) <= 0) {
          return;
        }
        burstFrame = requestAnimationFrame(burst);
      };
      burstFrame = requestAnimationFrame(burst);
    });
    const cleanup = () => {
      observer.disconnect();
      if (burstFrame !== null) {
        cancelAnimationFrame(burstFrame);
        burstFrame = null;
      }
      if (refocusCleanupRef.current === cleanup) {
        refocusCleanupRef.current = null;
      }
    };
    refocusCleanupRef.current = cleanup;
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const dispatchWithFocusReturn = (kind: AdminCommandKind, triggerEl: HTMLElement | null) => {
    onAction(kind);
    scheduleRefocusAfterModalClose(triggerEl);
  };

  const onOverflowClick: MenuProps["onClick"] = ({ key }) => {
    dispatchWithFocusReturn(key as AdminCommandKind, overflowTriggerRef.current);
  };

  return (
    <Flex vertical gap={12} className="admin-detail-panel">
      <div>
        <Flex align="center" gap={8} wrap>
          <Typography.Title level={4} style={{ marginBottom: 0 }}>
            {detail.name}
          </Typography.Title>
          <StatusTag status={detail.status} />
        </Flex>
        <Typography.Text type="secondary">
          Mã: {detail.code} · {classificationLabel}: {formatClassification(detail.classification)} · Thuộc quản lý:{" "}
          {detail.parentId != null ? (parentNodeName ?? "(ngoài cây hiển thị)") : "Cấp gốc"}
        </Typography.Text>
      </div>
      <ValidityBand versions={versions} currentVersion={detail.version} />
      <Flex gap={8} wrap className="admin-action-bar">
        {directActions.map((action) => (
          <Button
            key={action.kind}
            type={action.kind === "create" ? "primary" : "default"}
            onClick={(event) => dispatchWithFocusReturn(action.kind, event.currentTarget)}
          >
            {action.label}
          </Button>
        ))}
        {detail.status === "ACTIVE" ? (
          <Dropdown trigger={["click"]} menu={{ items: OVERFLOW_MENU_ITEMS, onClick: onOverflowClick }}>
            <Button ref={overflowTriggerRef}>Thao tác khác</Button>
          </Dropdown>
        ) : null}
        {detail.status === "MERGED" ? (
          <Typography.Text type="secondary">
            Đơn vị đã sáp nhập — chỉ xem được lịch sử, không thao tác thêm.
          </Typography.Text>
        ) : null}
      </Flex>
      <Collapse
        destroyOnHidden
        items={[
          {
            key: "history",
            label: "Lịch sử thay đổi",
            children: (
              <VersionHistoryTable
                versions={versions}
                currentVersion={detail.version}
                classificationLabel={classificationLabel}
              />
            ),
          },
        ]}
      />
    </Flex>
  );
}
